import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useGL } from '../hooks';
import { cn, formatCurrency } from '@/lib/utils';

export default function GLDetail() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('start') || '';
  const endDate = searchParams.get('end') || '';
  const accountName = searchParams.get('name') || 'Account';

  const { data, isLoading, error } = useGL(accountId ?? null, startDate, endDate);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/acc/pnl" className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4" />
          Back to P&amp;L
        </Link>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">Error: {(error as Error).message}</div>
      </div>
    );
  }

  const totalDebit = data?.transactions.reduce((sum, t) => sum + t.debit, 0) || 0;
  const totalCredit = data?.transactions.reduce((sum, t) => sum + t.credit, 0) || 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <Link to="/acc/pnl" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-2 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to P&amp;L
        </Link>
        <h1 className="text-2xl font-bold">{decodeURIComponent(accountName)}</h1>
        <p className="text-muted-foreground mt-1">
          {data?.startDate} to {data?.endDate} · {data?.transactions.length ?? 0} transactions
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-background rounded-xl shadow-sm border p-4">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Debits</span>
          <div className="text-2xl font-bold mt-1 tabular-nums">{formatCurrency(totalDebit)}</div>
        </div>
        <div className="bg-background rounded-xl shadow-sm border p-4">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Credits</span>
          <div className="text-2xl font-bold mt-1 tabular-nums">{formatCurrency(totalCredit)}</div>
        </div>
        <div className="bg-background rounded-xl shadow-sm border p-4">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Net</span>
          <div
            className={cn(
              'text-2xl font-bold mt-1 tabular-nums',
              totalDebit - totalCredit >= 0 ? 'text-green-600' : 'text-red-600',
            )}
          >
            {formatCurrency(totalDebit - totalCredit)}
          </div>
        </div>
      </div>

      <div className="bg-background rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left text-xs font-semibold text-muted-foreground">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Doc #</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Memo</th>
              <th className="px-4 py-3 text-right">Debit</th>
              <th className="px-4 py-3 text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {data?.transactions.map((txn, idx) => (
              <tr key={idx} className={cn('border-b', idx % 2 === 0 ? 'bg-background' : 'bg-muted/10')}>
                <td className="px-4 py-3 whitespace-nowrap">{txn.date}</td>
                <td className="px-4 py-3 text-muted-foreground">{txn.type}</td>
                <td className="px-4 py-3 text-muted-foreground">{txn.docNum}</td>
                <td className="px-4 py-3 font-medium">{txn.name}</td>
                <td className="px-4 py-3 text-muted-foreground truncate max-w-xs">{txn.memo}</td>
                <td className="px-4 py-3 text-right tabular-nums">{txn.debit > 0 ? formatCurrency(txn.debit) : ''}</td>
                <td className="px-4 py-3 text-right tabular-nums">{txn.credit > 0 ? formatCurrency(txn.credit) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted font-semibold">
            <tr>
              <td colSpan={5} className="px-4 py-3">Total</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(totalDebit)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(totalCredit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
